import { useEffect } from "react";
import "./TallyInPage.scss";
import { useLocation, useNavigate } from "react-router";
import { Button } from "react-bootstrap";
import { observer } from "mobx-react";
import { useOrder } from "../../Context/useOrder";
import dataStore from "../../Store/dataStore";
import tallyStore from "../../Store/tallyStore";
import { Order } from "../../Models/Order";
import { Product } from "../../Models/Product";
import { TallyDraft, sumTallyEffectQty } from "../../Models/TallyDraft";
import OrderStatusBar from "../../Components/OrderStatusBar/OrderStatusBar";
import { tallyRoutes } from "../../Helpers/TallyMode";

interface Props {}

const TallyInPage = observer((props: Props) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const routes = tallyRoutes(pathname);
  const { getCurrentOrder } = useOrder();
  const order: Order | null = getCurrentOrder();

  useEffect(() => {
    //nothing selected (e.g. the page was opened directly), go back to the list
    if (!order || !order.transRefCode) {
      navigate(routes.list);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  if (!order || !order.transRefCode) {
    return null;
  }

  const onLineSelected = (line: Product) => {
    navigate(routes.line, { state: { detailNo: line.detailNo } });
  };

  const cartLines: Product[] = order.cartLines || [];
  const drafts: TallyDraft[] = tallyStore.getDraftsForTransaction(dataStore.serverUrl || "", dataStore.dbKey || "", order.transRefCode);

  const getDraft = (line: Product): TallyDraft | undefined => {
    return drafts.find((d) => d.detailNo === line.detailNo);
  };

  return (
    <>
      <OrderStatusBar />
      <div className="container-limited-width tally-in-page">
        {order.accountRef && (
          <div className="row">
            <div className="col-12 account-ref">{order.accountRef}</div>
          </div>
        )}

        <div className="row">
          <div className="col-12 mb-2">
            <ul className="list-group orders-list">
              {cartLines.map((line, index) => {
                const draft = getDraft(line);
                const tallied = sumTallyEffectQty(draft);
                const tallyCount = draft ? draft.tallies.length : 0;
                return (
                  <li
                    key={`${line.detailNo}_${index}`}
                    className={`list-group-item d-flex justify-content-between align-items-center${
                      tallyCount > 0 ? " has-pending" : ""
                    }`}
                    title={tallyCount > 0 ? "Counted on device, not yet posted" : undefined}
                  >
                    <div className="d-flex flex-column line-info" onClick={() => onLineSelected(line)}>
                      <b className="line-desc">{line.stockDesc}</b>
                      {(line.linePalletNo || line.lineSku) && (
                        <span className="line-meta">
                          {line.linePalletNo && <>Pallet {line.linePalletNo}</>}
                          {line.linePalletNo && line.lineSku && " · "}
                          {line.lineSku && <>SKU {line.lineSku}</>}
                        </span>
                      )}
                      <span className="line-qty">
                        {(line.talliedCount || 0) + tallyCount} / {line.tallyTargetQty || 0}
                        {" · "}
                        {((line.talliedQty || 0) + tallied).toLocaleString(navigator.language, { maximumFractionDigits: 3 })} kg
                      </span>
                      {tallyCount > 0 && (
                        <span className="pending-status">
                          Pending Tally ({tallyCount})
                        </span>
                      )}
                    </div>
                    <div className="d-flex align-self-center align-items-center">
                      <Button variant="primary" size="sm" className="tally-btn" onClick={() => onLineSelected(line)}>
                        Tally
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
            {cartLines.length === 0 && <div className="my-3 text-center text-muted">This transaction has no lines.</div>}
          </div>
        </div>
      </div>
    </>
  );
});

export default TallyInPage;
